import {
  Badge,
  Button,
  Field,
  Flex,
  Heading,
  IconButton,
  Input,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { LuTrash } from 'react-icons/lu'
import { Challenges } from '../Challenges'
import EventInfo from '../EventInfo'
import { LinkWithQr } from '../LinkWithQr'
import { CrowdsColorPicker } from '../ui/CrowdsColorPicker'
import { generateValidatorCode, removeValidatorCode } from '../../services/firebaseService'
import { useTranslation } from '../../i18n'

export function ChallengesSection({ s }) {
  const { t } = useTranslation()
  const prompt = s.draft?.challengePrompt
  return (
    <Stack gap={5}>
      <Challenges form={s.draft} setForm={s.setDraft} editing={s.editing} />

      <Flex gap={4} align="flex-end" wrap="wrap">
        <Field.Root flex="1">
          <Field.Label>{t('events.challengePrompt')}</Field.Label>
          <Input
            value={prompt?.text ?? ''}
            onChange={(e) => s.setField('challengePrompt', { ...prompt, text: e.target.value })}
            disabled={!s.editing}
            placeholder={t('events.challengePromptPlaceholder')}
          />
        </Field.Root>
        <CrowdsColorPicker
          label={t('events.promptColor')}
          color={prompt?.color ?? '#ffffff'}
          editing={s.editing}
          setColor={(color) => s.setField('challengePrompt', { ...prompt, color })}
          maxW={{ base: '100%', sm: '200px' }}
        />
      </Flex>

      {/* A video cap only makes sense outside prize mode, where the prize
          already bounds how many videos a participant sends. */}
      {!s.isHidden('maxVideos') && !s.draft?.challengeMode && (
        <Field.Root>
          <Field.Label>{t('events.maxVideosPerUser')}</Field.Label>
          <Flex align="center" gap={3} wrap="wrap">
            <Input
              type="number"
              min={0}
              value={s.draft?.maxVideosPerUser ?? ''}
              onChange={(e) => s.setField('maxVideosPerUser', e.target.value === '' ? null : parseInt(e.target.value) || 0)}
              disabled={!s.editing}
              w={{ base: '100%', sm: '100px' }}
              placeholder={t('events.maxVideosPlaceholder')}
            />
            <Text fontSize="sm" color="fg.muted">{t('events.maxVideosHelp')}</Text>
          </Flex>
        </Field.Root>
      )}
    </Stack>
  )
}

function validatorUrlFor(eventId, code) {
  let base = import.meta.env.VITE_PUBLIC_VALIDATOR_URL || window.location.origin
  if (import.meta.env.DEV) {
    const { protocol, hostname } = window.location
    base = `${protocol}//${hostname}:5176`
  }
  return `${base}/validate?event=${eventId}&code=${code}`
}

export function PrizeSection({ s }) {
  const { t } = useTranslation()
  const d = s.draft
  return (
    <Stack gap={5}>
      <Flex align="center" gap={3} wrap="wrap">
        <Switch.Root
          checked={d?.challengeMode ?? false}
          onCheckedChange={(details) => s.setField('challengeMode', details.checked)}
          disabled={!s.editing}
        >
          <Switch.HiddenInput />
          <Switch.Control><Switch.Thumb /></Switch.Control>
          <Switch.Label>{t('events.enablePrizeMode')}</Switch.Label>
        </Switch.Root>
        <Text fontSize="sm" color="fg.muted">{t('events.enablePrizeModeHelp')}</Text>
      </Flex>

      {d?.challengeMode && (
        <Stack gap={4} pl={{ base: 0, md: 4 }}>
          <Flex align="center" gap={3} wrap="wrap">
            <Switch.Root
              checked={d?.requireAuth ?? false}
              onCheckedChange={(details) => s.setField('requireAuth', details.checked)}
              disabled={!s.editing}
            >
              <Switch.HiddenInput />
              <Switch.Control><Switch.Thumb /></Switch.Control>
              <Switch.Label>{t('events.requireLogin')}</Switch.Label>
            </Switch.Root>
            <Text fontSize="sm" color="fg.muted">{t('events.requireLoginHelp')}</Text>
          </Flex>

          <Field.Root>
            <Field.Label>{t('events.maxRejections')}</Field.Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={d?.maxRejections ?? 3}
              onChange={(e) => s.setField('maxRejections', parseInt(e.target.value) || 3)}
              disabled={!s.editing}
              w={{ base: '100%', sm: '100px' }}
            />
          </Field.Root>

          <Heading size="sm">{t('events.prizeConfig')}</Heading>
          <Field.Root>
            <Field.Label>{t('events.prizeTitle')}</Field.Label>
            <Input
              value={d?.prizeConfig?.title ?? ''}
              onChange={(e) => s.setField('prizeConfig', { ...d?.prizeConfig, title: e.target.value })}
              disabled={!s.editing}
              placeholder={t('events.prizeTitlePlaceholder')}
            />
          </Field.Root>
          <Field.Root>
            <Field.Label>{t('events.prizeDescription')}</Field.Label>
            <Textarea
              value={d?.prizeConfig?.description ?? ''}
              onChange={(e) => s.setField('prizeConfig', { ...d?.prizeConfig, description: e.target.value })}
              disabled={!s.editing}
              placeholder={t('events.prizeDescriptionPlaceholder')}
            />
          </Field.Root>
          <Field.Root>
            <Field.Label>{t('events.prizePrompt')}</Field.Label>
            <Textarea
              value={d?.prizePrompt ?? ''}
              onChange={(e) => s.setField('prizePrompt', e.target.value)}
              disabled={!s.editing}
              placeholder={t('events.prizePromptPlaceholder')}
            />
          </Field.Root>

          {/* Codes are written on the spot by their own calls, not by Save, so
              they can only be managed once the event exists. */}
          {s.eventId && (
            <>
              <Heading size="sm">{t('events.validatorCodes')}</Heading>
              <Text fontSize="sm" color="fg.muted">{t('events.validatorCodesHelp')}</Text>
              <Stack gap={2}>
                {(s.event?.validatorCodes || []).map((vc) => (
                  <LinkWithQr
                    key={vc.code}
                    url={validatorUrlFor(s.eventId, vc.code)}
                    qrTitle={t('events.validatorQrTitle')}
                    downloadName={`validator-qr-${s.eventId}`}
                    copyLabel={t('events.copyValidatorUrl')}
                    leading={<Badge fontSize="md" px={2} fontFamily="mono">{vc.code}</Badge>}
                  >
                    {s.editing && (
                      <IconButton
                        aria-label={t('events.removeCode')}
                        size="xs"
                        variant="ghost"
                        onClick={() => removeValidatorCode(s.eventId, vc)}
                      >
                        <LuTrash />
                      </IconButton>
                    )}
                  </LinkWithQr>
                ))}
              </Stack>
              {s.editing && (
                <Button size="sm" variant="outline" alignSelf="flex-start" onClick={() => generateValidatorCode(s.eventId)}>
                  {t('events.generateValidatorCode')}
                </Button>
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export function InfoSection({ s }) {
  return (
    <EventInfo
      loading={s.info.loading}
      editing={s.editing}
      canEdit={s.canEdit}
      markdown={s.info.markdown}
      onChange={s.info.setMarkdown}
    />
  )
}
